import axios from "axios";
import thirdPartyCookiesEnabled from "../../shared/cookiecheck/cookie-check";
import { extendAlmaGlobal, getLocalStorageObject, getLocalStorageString, setLocalStorageString, } from "../../utils";
import Service from "../service";
const GRAVITO_OPUID_KEY = "gravitoOPUid";
const AMID_AVAILABLE_EVENT_NAME = "alma_master_id_available";
const AMID_REPETITIVE_EVENT_NAME = "alma_master_id_available_repetitive";
const GRAVITO_LOCAL_PROFILE_KEY = "gravitoSharedProfile";
const GRAVITO_LAST_CONTACT_KEY = "gravitoLastContact";
// In seconds, 30 mins
const GRAVITO_SYNC_INTERVAL = 1800;
const defaultConfiguration = {
    enabled: true,
    defaults: {
        gravitoEndpoint: "https://handshake.gravito.net/api",
        timeout: 1500,
        interval: 1860000,
        shouldSendOPUid: thirdPartyCookiesEnabled,
        broadcastInterval: 500,
    },
};
class GravitoService extends Service {
    constructor(configuration, context) {
        super("Gravito", context);
        this.isBroadcasting = false;
        // eslint-disable-next-line no-undef
        this.broadcastInterval = 0;
        // eslint-disable-next-line no-undef
        this.syncGravitoTimeout = 0;
        this.configuration = { ...defaultConfiguration, ...configuration };
        this.axiosInstance = axios.create();
    }
    async bootstrapService() {
        this.logger.logInfo(`Bootstrapping Gravito service`);
        this.logger.perfEventStart("gravito");
        const syncHandler = async () => {
            const secsSinceLastContact = GravitoService.getSecondsSinceLastSync();
            this.logger.logInfo(`Last contact was ${secsSinceLastContact} seconds ago`);
            if (localStorage.getItem("gravitoLastContact") === null ||
                secsSinceLastContact > GRAVITO_SYNC_INTERVAL) {
                this.logger.logInfo(`${secsSinceLastContact} since last contact`);
                await this.syncGravito();
                setLocalStorageString(GRAVITO_LAST_CONTACT_KEY, new Date().getTime().toString(10));
            }
            else {
                this.logger.logInfo("Stored timestamp is too fresh, skip API request");
            }
            const nextSync = GRAVITO_SYNC_INTERVAL - secsSinceLastContact + 1;
            this.logger.logInfo(`Next sync scheduled in ${nextSync} seconds`);
            this.logger.logInfo(`Adding timeout handler to sync`);
            this.syncGravitoTimeout = setTimeout(syncHandler, nextSync * 1000);
        };
        await syncHandler();
        if (getLocalStorageString(GRAVITO_OPUID_KEY)) {
            extendAlmaGlobal({ amid: true });
            this.broadcastAlmaMasterId();
        }
        const profile = getLocalStorageObject(GRAVITO_LOCAL_PROFILE_KEY) ?? undefined;
        this.passConsentToCmp(profile);
    }
    static getSecondsSinceLastSync() {
        const lastContact = Number.parseInt(getLocalStorageString(GRAVITO_LAST_CONTACT_KEY) ?? "0", 10);
        const secsSinceLastContact = (new Date().getTime() - lastContact) / 1000;
        return secsSinceLastContact;
    }
    broadcastAlmaMasterId() {
        const amidAvailableEvent = new CustomEvent(AMID_AVAILABLE_EVENT_NAME, {
            detail: getLocalStorageString(GRAVITO_OPUID_KEY),
        });
        window.dispatchEvent(amidAvailableEvent);
        if (!this.isBroadcasting) {
            this.logger.logInfo(`Starting Alma Master Id broadcast`);
            const broadcast = () => {
                const amidRepetitiveEvent = new CustomEvent(AMID_REPETITIVE_EVENT_NAME, {
                    detail: getLocalStorageString(GRAVITO_OPUID_KEY),
                });
                window.dispatchEvent(amidRepetitiveEvent);
            };
            this.broadcastInterval = setInterval(broadcast, this.configuration.defaults.broadcastInterval);
            this.isBroadcasting = true;
        }
    }
    stopBroadcast() {
        if (this.isBroadcasting) {
            this.logger.logInfo(`Stopping Alma Master Id broadcast`);
            // eslint-disable-next-line no-undef
            clearInterval(this.broadcastInterval);
        }
    }
    passConsentToCmp(profile) {
        this.logger.logInfo(`Attempting to pass consent to CMP`);
        this.logger.perfEventEnd("gravito");
        if (this.context.services.almaCmpService) {
            this.context.services.almaCmpService.passExternalConsent(profile?.gravitoData);
        }
        else if (window.ALMA.cmpV2 &&
            typeof window.ALMA.cmpV2.passConsent === "function") {
            if (profile) {
                this.logger.logInfo(`Found CMPv2, passing profile ${JSON.stringify(profile, null, 2)}`);
            }
            else {
                this.logger.logInfo(`Did not find a profile, passing control to CMP`);
            }
            window.ALMA.cmpV2.passConsent(profile);
        }
        else {
            this.logger.logError(`Could not find a valid CMP to pass consent to`);
        }
    }
    async syncGravito() {
        try {
            this.logger.logInfo(`Checking if we need to send possible OPUid...`);
            const sendLocalOPUid = await this.configuration.defaults.shouldSendOPUid();
            let localOPUid;
            if (sendLocalOPUid) {
                this.logger.logInfo(`Fetching OPUid from localStorage`);
                localOPUid = getLocalStorageString(GRAVITO_OPUID_KEY) ?? undefined;
            }
            else {
                this.logger.logInfo(`No need to send OPUid`);
            }
            if (localOPUid) {
                this.logger.logInfo(`OPUid from localStorage found!`);
            }
            this.logger.logInfo(`Starting handshake with Gravito`);
            const { data: gravitoResponse } = await this.gravitoHandshake(localOPUid ?? undefined);
            if (gravitoResponse) {
                this.logger.logInfo(`Gravito responded with: ${JSON.stringify(gravitoResponse, null, 2)}`);
                if (gravitoResponse.gravitoOpUid) {
                    setLocalStorageString(GRAVITO_OPUID_KEY, gravitoResponse.gravitoOpUid);
                }
                if (gravitoResponse?.sharedConsolidatedProfile) {
                    if (Object.prototype.hasOwnProperty.call(gravitoResponse.sharedConsolidatedProfile, "gravitoData")) {
                        this.logger.logInfo(`Received shared profile from Gravito, saving to ls`);
                        setLocalStorageString(GRAVITO_LOCAL_PROFILE_KEY, JSON.stringify(gravitoResponse?.sharedConsolidatedProfile));
                    }
                    else {
                        this.logger.logWarn(`Gravito responded with empty shared consent`);
                    }
                }
            }
        }
        catch (e) {
            this.logger.logWarn(`Could not perform Gravito handshake due to ${e}`);
        }
    }
    async gravitoHandshake(gravitoOPUid) {
        return this.axiosInstance.post(`${this.configuration.defaults?.gravitoEndpoint}/ap`, undefined, {
            params: { includeProfile: true, includeSharedProfileHistory: true },
            timeout: this.configuration.defaults?.timeout,
            withCredentials: true,
            headers: gravitoOPUid ? { "g-op-uid": gravitoOPUid } : undefined,
        });
    }
    tagObservation(params) {
        this.logger.logInfo(`Tagging observation ${JSON.stringify(params, null, 2)} to Gravito`);
        const queryParams = new URLSearchParams(params);
        const url = `${this.configuration.defaults.gravitoEndpoint}/listen?${queryParams.toString()}`;
        new Image().src = url;
        this.logger.logInfo(`Observation tagged`);
    }
}
export default GravitoService;
//# sourceMappingURL=gravito.js.map